import { Icon, Label, token } from "@may-i/mash-design-system";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import type { Camera } from "~/entities/camera/types";
import type { CountingLine, IntervalZone } from "~/entities/analysis-config/types";
import { EmptyImagePlaceholder } from "~/shared/ui/EmptyImagePlaceholder";
import { LineZoneCard } from "./LineZoneCard";

const LINE_TEXT = "#31B400";
const LINE_BG = "rgba(99, 251, 42, 0.16)";
const ZONE_TEXT = "#0090A3";
const ZONE_BG = "rgba(0, 224, 253, 0.16)";

interface CameraCardProps {
  camera: Camera;
  displayName: string;
  isAnalyzed: boolean;
  isDuplicateNumber: boolean;
  emptyNumberLabel?: string;
  selected: boolean;
  selectedLineId: number | null;
  selectedZoneId: number | null;
  countingLines: CountingLine[];
  intervalZones: IntervalZone[];
  globalDuplicateKeys: Set<string>;
  crossCameraDuplicateNames: Set<string>;
  duplicateNameLabel: string;
  emptyNameLabel: string;
  crossCameraDuplicateNameLabel: string;
  duplicateKeyLabel: string;
  emptyKeyLabel: string;
  invalidKeyFormatLabel: string;
  memoCount?: number;
  missingFloorPlanLineHint?: string;
  missingFloorPlanZoneHint?: string;
  missingSnapshotLineHint?: string;
  missingSnapshotZoneHint?: string;
  onClick: () => void;
  onMemoClick?: () => void;
  onUpdateLineName: (lineId: number, name: string) => void;
  onUpdateLineKey: (lineId: number, key: string) => void;
  onDeleteLine: (lineId: number) => void;
  onUpdateZoneName: (zoneId: number, name: string) => void;
  onUpdateZoneKey: (zoneId: number, key: string) => void;
  onDeleteZone: (zoneId: number) => void;
  onSelectLine?: (lineId: number) => void;
  onSelectZone?: (zoneId: number) => void;
}

export function CameraCard({
  camera,
  displayName,
  isAnalyzed,
  isDuplicateNumber,
  emptyNumberLabel,
  selected,
  selectedLineId,
  selectedZoneId,
  countingLines,
  intervalZones,
  globalDuplicateKeys,
  crossCameraDuplicateNames,
  duplicateNameLabel,
  emptyNameLabel,
  crossCameraDuplicateNameLabel,
  duplicateKeyLabel,
  emptyKeyLabel,
  invalidKeyFormatLabel,
  memoCount = 0,
  missingFloorPlanLineHint,
  missingFloorPlanZoneHint,
  missingSnapshotLineHint,
  missingSnapshotZoneHint,
  onClick,
  onMemoClick,
  onUpdateLineName,
  onUpdateLineKey,
  onDeleteLine,
  onUpdateZoneName,
  onUpdateZoneKey,
  onDeleteZone,
  onSelectLine,
  onSelectZone,
}: CameraCardProps) {
  const { t } = useTranslation("analysis-draft");
  const lineCount = countingLines.length;
  const zoneCount = intervalZones.length;
  const hasItems = lineCount > 0 || zoneCount > 0;

  // 같은 카메라 내 이벤트 소스 이름 중복 집합
  const duplicateNames = useMemo(() => {
    const allNames = [
      ...countingLines.map((l) => l.analysisConfig?.name ?? ""),
      ...intervalZones.map((z) => z.analysisConfig?.name ?? ""),
    ].filter((n) => n !== "");
    const seen = new Set<string>();
    const dupes = new Set<string>();
    for (const n of allNames) {
      if (seen.has(n)) dupes.add(n);
      seen.add(n);
    }
    return dupes;
  }, [countingLines, intervalZones]);

  // 빈 키가 있는지 검사
  const hasEmptyKey = useMemo(() => {
    return [...countingLines, ...intervalZones].some((item) => {
      const parts = item.key.split("_");
      const suffix = parts.length > 2 ? parts.slice(2).join("_") : parts.at(-1) ?? "";
      return suffix.trim() === "";
    });
  }, [countingLines, intervalZones]);

  const hasEmptyName = [...countingLines, ...intervalZones].some((item) => (item.analysisConfig?.name ?? "").trim() === "");

  const isEmptyNumber = displayName.replace(/^N/, "") === "";
  const hasDuplicateKey = countingLines.some((l) => globalDuplicateKeys.has(l.key)) || intervalZones.some((z) => globalDuplicateKeys.has(z.key));
  const hasInvalidKeyFormat = [...countingLines, ...intervalZones].some((item) => {
    const hasAnalysis = !!item.analysisConfig && item.analysisConfig.coordinates.length > 0;
    if (!hasAnalysis) return false;
    const parts = item.key.split("_");
    const suffix = parts.length > 2 ? parts.slice(2).join("_") : parts.at(-1) ?? "";
    return suffix.trim() !== "" && !/^[a-zA-Z0-9-]*$/.test(suffix);
  });
  const hasError = isDuplicateNumber || isEmptyNumber || hasDuplicateKey || hasEmptyKey || hasInvalidKeyFormat || duplicateNames.size > 0 || hasEmptyName;

  return (
    <Container $selected={selected} $hasError={hasError} onClick={onClick} data-camera-id={camera.id}>
      <HeaderRow>
        <LeftInfo>
          {isEmptyNumber ? (
            <CameraNumber $hasError>{emptyNumberLabel ?? "—"}</CameraNumber>
          ) : (
            <CameraNumber $hasError={isDuplicateNumber}>{displayName}</CameraNumber>
          )}
          {isAnalyzed ? (
            <Label variant="soft" color="success" size="xSmall">
              {t("label.analyzed")}
            </Label>
          ) : (
            <Label variant="filled" color="default" size="xSmall">
              {t("label.notAnalyzed")}
            </Label>
          )}
        </LeftInfo>
        <RightInfo>
          <StyledLabel $text={LINE_TEXT} $bg={LINE_BG} variant="soft" color="default" size="small">
            line: {lineCount}
          </StyledLabel>
          <StyledLabel $text={ZONE_TEXT} $bg={ZONE_BG} variant="soft" color="default" size="small">
            zone: {zoneCount}
          </StyledLabel>
          <MemoCountButton
            onClick={(e) => {
              e.stopPropagation();
              onMemoClick?.();
            }}
          >
            <Icon icon="ic_chart_description" width={14} height={14} />
            <span>{memoCount}</span>
          </MemoCountButton>
        </RightInfo>
      </HeaderRow>
      <BodyRow>
        <Thumbnail>
          {camera.previewImageUrl ? (
            <ThumbnailImage src={camera.previewImageUrl} alt={displayName} />
          ) : (
            <EmptyImagePlaceholder />
          )}
        </Thumbnail>
        <LineZoneList>
          {countingLines.map((line) => {
            const keyParts = line.key.split("_");
            const keySuffix = keyParts.length > 2 ? keyParts.slice(2).join("_") : keyParts.at(-1) ?? "";
            const keyPrefix = keyParts.length > 2 ? keyParts.slice(0, 2).join("_") + "_" : "line_default_";
            // 스냅샷에 좌표가 있는 라인만 에러 검증 — draft 상태(좌표 없음/도면만)는 이름/키 미입력 OK
            const hasAnalysis = !!line.analysisConfig && line.analysisConfig.coordinates.length > 0;
            // PO QA 8차 bi: 라인/구역은 스냅샷·도면 양쪽 모두 그려져야 정상(QA-ISSUES 4-2-1).
            // 각 면에 "그려질 수 있는 유효 좌표(line >= 2)"가 없으면 그 면 추가를 안내. 좌표가 0이든 1이든(렌더 불가) 모두 잡힘.
            // 각 면을 독립 판정하므로 스냅샷/도면 안내가 섞이지 않음(한 면만 누락이면 그 면만, 둘 다 누락이면 둘 다).
            const hasSnapshotShape = !!line.analysisConfig && line.analysisConfig.coordinates.length >= 2;
            const hasFloorShape = !!line.floorPlanConfig && line.floorPlanConfig.coordinates.length >= 2;
            const isMissingSnapshot = !hasSnapshotShape; // 스냅샷에 유효 도형 없음 → "스냅샷에도 추가"
            const isMissingFloorPlan = !hasFloorShape; // 도면에 유효 도형 없음 → "도면에도 추가"
            // key 포맷 위반 — 빈 값은 isEmptyKey 가 따로 잡고, 빈 게 아닐 때만 정규식 검사 (PO QA 2차 m)
            const isInvalidKeyFormat = hasAnalysis && keySuffix.trim() !== "" && !/^[a-zA-Z0-9-]*$/.test(keySuffix);
            return (
              <LineZoneCard
                key={line.id}
                id={line.id}
                type="line"
                name={line.analysisConfig?.name ?? ""}
                keySuffix={keySuffix}
                keyPrefix={keyPrefix}
                isDuplicateName={hasAnalysis && duplicateNames.has(line.analysisConfig?.name ?? "")}
                isEmptyName={hasAnalysis && (line.analysisConfig?.name ?? "").trim() === ""}
                isCrossCameraDuplicateName={hasAnalysis && crossCameraDuplicateNames.has(line.analysisConfig?.name ?? "")}
                duplicateNameLabel={duplicateNameLabel}
                emptyNameLabel={emptyNameLabel}
                crossCameraDuplicateNameLabel={crossCameraDuplicateNameLabel}
                isDuplicateKey={hasAnalysis && globalDuplicateKeys.has(line.key)}
                isEmptyKey={hasAnalysis && keySuffix.trim() === ""}
                isInvalidKeyFormat={isInvalidKeyFormat}
                duplicateKeyLabel={duplicateKeyLabel}
                emptyKeyLabel={emptyKeyLabel}
                invalidKeyFormatLabel={invalidKeyFormatLabel}
                missingFloorPlanHint={isMissingFloorPlan ? missingFloorPlanLineHint : undefined}
                missingSnapshotHint={isMissingSnapshot ? missingSnapshotLineHint : undefined}
                selected={selectedLineId === line.id}
                onClick={() => onSelectLine?.(line.id)}
                onNameChange={(name) => onUpdateLineName(line.id, name)}
                onKeyChange={(key) => onUpdateLineKey(line.id, key)}
                onDelete={() => onDeleteLine(line.id)}
              />
            );
          })}
          {intervalZones.map((zone) => {
            const keyParts = zone.key.split("_");
            const keySuffix = keyParts.length > 2 ? keyParts.slice(2).join("_") : keyParts.at(-1) ?? "";
            const keyPrefix = keyParts.length > 2 ? keyParts.slice(0, 2).join("_") + "_" : "zone_default_";
            const hasAnalysis = !!zone.analysisConfig && zone.analysisConfig.coordinates.length > 0;
            // PO QA 8차 bi: 구역도 라인과 동일 — 각 면 유효 좌표(구역 >= 3) 없으면 그 면 추가 안내 (독립 판정)
            const hasSnapshotShape = !!zone.analysisConfig && zone.analysisConfig.coordinates.length >= 3;
            const hasFloorShape = !!zone.floorPlanConfig && zone.floorPlanConfig.coordinates.length >= 3;
            const isMissingSnapshot = !hasSnapshotShape;
            const isMissingFloorPlan = !hasFloorShape;
            const isInvalidKeyFormat = hasAnalysis && keySuffix.trim() !== "" && !/^[a-zA-Z0-9-]*$/.test(keySuffix);
            return (
              <LineZoneCard
                key={zone.id}
                id={zone.id}
                type="zone"
                name={zone.analysisConfig?.name ?? ""}
                keySuffix={keySuffix}
                keyPrefix={keyPrefix}
                isDuplicateName={hasAnalysis && duplicateNames.has(zone.analysisConfig?.name ?? "")}
                isEmptyName={hasAnalysis && (zone.analysisConfig?.name ?? "").trim() === ""}
                isCrossCameraDuplicateName={hasAnalysis && crossCameraDuplicateNames.has(zone.analysisConfig?.name ?? "")}
                duplicateNameLabel={duplicateNameLabel}
                emptyNameLabel={emptyNameLabel}
                crossCameraDuplicateNameLabel={crossCameraDuplicateNameLabel}
                isDuplicateKey={hasAnalysis && globalDuplicateKeys.has(zone.key)}
                isEmptyKey={hasAnalysis && keySuffix.trim() === ""}
                isInvalidKeyFormat={isInvalidKeyFormat}
                duplicateKeyLabel={duplicateKeyLabel}
                emptyKeyLabel={emptyKeyLabel}
                invalidKeyFormatLabel={invalidKeyFormatLabel}
                missingFloorPlanHint={isMissingFloorPlan ? missingFloorPlanZoneHint : undefined}
                missingSnapshotHint={isMissingSnapshot ? missingSnapshotZoneHint : undefined}
                selected={selectedZoneId === zone.id}
                onClick={() => onSelectZone?.(zone.id)}
                onNameChange={(name) => onUpdateZoneName(zone.id, name)}
                onKeyChange={(key) => onUpdateZoneKey(zone.id, key)}
                onDelete={() => onDeleteZone(zone.id)}
              />
            );
          })}
          {!hasItems && (
            <PlaceholderText>{t("label.noLineZone")}</PlaceholderText>
          )}
        </LineZoneList>
      </BodyRow>
    </Container>
  );
}

// --- Styles ---

const Container = styled.div<{ $selected: boolean; $hasError: boolean }>`
  display: flex;
  flex-direction: column;
  width: 444px;
  border-radius: 8px;
  border: 1px solid ${({ $selected, $hasError }) => {
    if ($hasError) return $selected ? "#FA332D" : "#F44667";
    return $selected ? "#484BD2" : "rgba(143, 149, 178, 0.32)";
  }};
  box-shadow: ${({ $selected, $hasError }) => {
    if (!$selected) return "none";
    return $hasError ? "0 0 0 1px #FA332D" : "0 0 0 1px #484BD2";
  }};
  background: #fff;
  cursor: pointer;
  transition: border-color 0.15s;

  &:hover {
    border-color: ${({ $selected, $hasError }) => {
      if ($hasError) return $selected ? "#FA332D" : "#F44667";
      return $selected ? "#484BD2" : "rgba(143, 149, 178, 0.48)";
    }};
  }
`;

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  padding: 8px 12px 0 14px;
`;

const LeftInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const CameraNumber = styled.span<{ $hasError?: boolean }>`
  font-family: Pretendard Variable, Pretendard, sans-serif;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.57;
  color: ${({ $hasError }) => ($hasError ? "#FA332D" : token("colors.text.primary"))};
`;

const RightInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const StyledLabel = styled(Label)<{ $text: string; $bg: string }>`
  --label-color: ${({ $text }) => $text};
  --label-background-color: ${({ $bg }) => $bg};
`;

const BodyRow = styled.div`
  display: flex;
  gap: 12px;
  padding: 6px 12px 12px;
`;

const Thumbnail = styled.div`
  width: 160px;
  height: 90px;
  flex-shrink: 0;
  border-radius: 4px;
  border: 0.75px solid rgba(143, 149, 178, 0.32);
  overflow: hidden;
`;

const ThumbnailImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const LineZoneList = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  gap: 12px;
  flex: 1;
  min-width: 0;
`;

const MemoCountButton = styled.button`
  display: flex;
  align-items: center;
  gap: 2px;
  flex-shrink: 0;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  color: ${token("colors.text.secondary")};

  > span {
    font-family: Pretendard Variable, Pretendard, sans-serif;
    font-size: 12px;
    font-weight: 600;
    line-height: 1.5;
  }

  &:hover {
    color: ${token("colors.text.primary")};
  }
`;

const PlaceholderText = styled.span`
  font-family: Pretendard Variable, Pretendard, sans-serif;
  font-size: 12px;
  color: ${token("colors.text.secondary")};
`;
